import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import MarketShell from '../components/market/MarketShell';
import { marketImageUrl } from '../lib/marketCatalog';
import { formatSar } from '../lib/marketPricing';
import {
  cancelOrder, confirmDelivery, CUSTOMER_STEPS, customerStep, getCheckoutConfig, getOrder, MARKET_API_IS_LOCAL, openDispute, startPayment, stepLabel, verifyPayment,
} from '../lib/marketOrdersApi';
import PreviewBanner from '../components/market/PreviewBanner';
import { Row } from './MarketCart';
import { track } from '../lib/growthTracker';

// One market order, as its buyer sees it: seven simple steps (received →
// processing → purchased → warehouse → preparing → shipped → delivered, the
// same mapping as public.market_customer_step), items,
// what was charged, payment, delivery address, tracking and timeline, and the
// buyer's own actions (pay / cancel while unpaid, confirm receipt, report a
// problem). Returning from the payment page (?payment=…) asks the server to
// verify; the page never decides that a payment succeeded.
//
// Tracking is labelled by source: events from the warehouse/carrier systems,
// and updates entered by MAABAR staff — never presented as carrier scans.

const T = {
  ar: {
    back: 'كل الطلبات', notFound: 'الطلب غير موجود.', preview: 'بيئة اختبار.',
    items: 'المنتجات', charges: 'المبالغ', goods: 'قيمة البضاعة', shipping: 'الشحن إلى السعودية', shippingLater: 'يُحدَّد بعد المراجعة', products: 'المنتجات', shipCustoms: 'الشحن والجمارك',
    servicesLine: 'الخدمات الإضافية', fee: 'رسوم الخدمة', vat: 'ضريبة القيمة المضافة', total: 'الإجمالي', importLine: 'ضريبة الاستيراد والتخليص',
    delivery: 'عنوان التوصيل', tracking: 'تتبّع الشحنة', noTracking: 'ستظهر تحديثات الشحن هنا عند توفرها.',
    srcProvider: 'من المستودع / شركة الشحن', srcAdmin: 'تحديث من فريق معبر', carrier: 'شركة الشحن', trackingNo: 'رقم التتبع',
    legDomestic: 'من المورد إلى المستودع', legIntl: 'إلى السعودية',
    timeline: 'سجل الطلب', payment: 'الدفع', payNow: 'ادفع الآن', payBy: (t) => `أكمل الدفع قبل ${t}`, paymentsOff: 'الدفع غير مفعّل بعد.',
    payTest: 'دفع تجريبي', paid: 'مدفوع', failed: 'لم يكتمل', created: 'بدأ',
    verifying: 'جارٍ التحقق من الدفع…', verifiedOk: 'تم استلام طلبك بنجاح',
    placed: 'تم استلام طلبك بنجاح', awaitingShip: 'نراجع تكلفة شحن طلبك الآن. سنشعرك بالإجمالي النهائي لتدفعه مرة واحدة.',
    refundPending: 'تعذّر تنفيذ طلبك. نعيد لك كامل المبلغ، وسيظهر الاسترداد بعد تأكيده من جهة الدفع.', refundDone: 'تمت إعادة كامل المبلغ.', verifiedNo: 'لم يكتمل الدفع. يمكنك المحاولة مرة أخرى.',
    cancel: 'إلغاء الطلب', cancelReason: 'سبب الإلغاء (اختياري)', confirmCancel: 'تأكيد الإلغاء',
    received: 'استلمت الطلب', problem: 'الإبلاغ عن مشكلة', problemReason: 'صف المشكلة (10 أحرف على الأقل)', send: 'إرسال',
    cancelled: 'تم إلغاء الطلب.', error: 'تعذّر تنفيذ الطلب. حاول مرة أخرى.',
  },
  en: {
    back: 'All orders', notFound: 'Order not found.', preview: 'Test environment.',
    items: 'Items', charges: 'Charges', goods: 'Goods', shipping: 'Shipping to Saudi Arabia', shippingLater: 'Set after review', products: 'Products', shipCustoms: 'Shipping and customs',
    servicesLine: 'Extra services', fee: 'Service fee', vat: 'VAT', total: 'Total', importLine: 'Import VAT and customs clearance',
    delivery: 'Delivery address', tracking: 'Shipment tracking', noTracking: 'Shipping updates will appear here when available.',
    srcProvider: 'From the warehouse / carrier', srcAdmin: 'Update from the MAABAR team', carrier: 'Carrier', trackingNo: 'Tracking number',
    legDomestic: 'Supplier to warehouse', legIntl: 'To Saudi Arabia',
    timeline: 'Order history', payment: 'Payment', payNow: 'Pay now', payBy: (t) => `Complete payment before ${t}`, paymentsOff: 'Payment is not enabled yet.',
    payTest: 'Test payment', paid: 'Paid', failed: 'Not completed', created: 'Started',
    verifying: 'Verifying your payment…', verifiedOk: 'Your order has been received successfully',
    placed: 'Your order has been received successfully', awaitingShip: 'We are pricing the shipping for your order now. We will let you know the final total to pay once.',
    refundPending: 'We could not fulfil your order. You will receive a full refund; it shows here once the payment provider confirms it.', refundDone: 'You have been refunded in full.', verifiedNo: 'The payment did not complete. You can try again.',
    cancel: 'Cancel order', cancelReason: 'Reason (optional)', confirmCancel: 'Confirm cancellation',
    received: 'I received the order', problem: 'Report a problem', problemReason: 'Describe the problem (at least 10 characters)', send: 'Send',
    cancelled: 'The order was cancelled.', error: 'Could not complete the request. Please try again.',
  },
  zh: {
    back: '全部订单', notFound: '订单不存在。', preview: '测试环境。',
    items: '商品', charges: '费用', goods: '货品', shipping: '运至沙特', shippingLater: '审核后确定', products: '产品', shipCustoms: '运费和关税',
    servicesLine: '增值服务', fee: '服务费', vat: '增值税', total: '合计', importLine: '进口增值税及清关费',
    delivery: '收货地址', tracking: '物流跟踪', noTracking: '物流更新将在此显示。',
    srcProvider: '来自仓库/承运商', srcAdmin: 'MAABAR 团队更新', carrier: '承运商', trackingNo: '运单号',
    legDomestic: '供应商至仓库', legIntl: '至沙特',
    timeline: '订单记录', payment: '付款', payNow: '立即付款', payBy: (t) => `请在 ${t} 前完成付款`, paymentsOff: '付款尚未开放。',
    payTest: '测试付款', paid: '已付款', failed: '未完成', created: '已发起',
    verifying: '正在核实付款…', verifiedOk: '已成功收到您的订单',
    placed: '已成功收到您的订单', awaitingShip: '我们正在核算您订单的运费，完成后会通知您最终总额，只需付款一次。',
    refundPending: '无法完成您的订单，将全额退款；支付机构确认后在此显示。', refundDone: '已全额退款。', verifiedNo: '付款未完成，可重试。',
    cancel: '取消订单', cancelReason: '取消原因（可选）', confirmCancel: '确认取消',
    received: '我已收货', problem: '报告问题', problemReason: '描述问题（至少 10 个字符）', send: '提交',
    cancelled: '订单已取消。', error: '操作失败，请重试。',
  },
};

const BADGE = {
  pending_payment: ['#FFF8E6', '#7A5A22'], disputed: ['#FFF4F2', '#8A2A1C'], refund_pending: ['#FFF4F2', '#8A2A1C'],
  cancelled: ['#F2F1EE', '#6B6760'], expired: ['#F2F1EE', '#6B6760'], refunded: ['#F2F1EE', '#6B6760'],
  completed: ['#EAF5EC', '#2E7D32'], delivered: ['#EAF5EC', '#2E7D32'],
};
export function StatusBadge({ status, label }) {
  const [bg, fg] = BADGE[status] || ['#EEF2F8', '#2B4A7A'];
  return <span data-status={status} style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, background: bg, color: fg, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>;
}

// The products as the buyer saw them (owner, 2026-11-02): goods + the fee, never the fee on its own line.
const orderProducts = (o) => Math.round((Number(o.goods_sar) + Number(o.service_fee_sar || 0)) * 100) / 100;
// each line scaled the same way (goods → products), so the lines add up to «المنتجات»
const feeUp = (o) => (Number(o.goods_sar) > 0 ? orderProducts(o) / Number(o.goods_sar) : 1);

export default function MarketOrderDetail({ lang = 'ar', user }) {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  usePageTitle('market', lang);
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState('');
  const [mode, setMode] = useState('');           // 'cancel' | 'dispute'
  const [text, setText] = useState('');
  const verified = useRef(false);

  const load = useCallback(async () => { setData(await getOrder(id)); }, [id]);
  useEffect(() => { load(); getCheckoutConfig().then((r) => setConfig(r.ok ? r.data : null)); }, [load]);
  // Straight from checkout without payment (shipping to be priced, or payments off).
  useEffect(() => {
    if (params.get('placed') !== '1') return;
    setNotice({ kind: 'ok', text: c.placed });
    setParams({}, { replace: true });
  }, [params, c, setParams]);

  // Back from the payment page: ask the server, once.
  useEffect(() => {
    const payment = params.get('payment');
    if (!payment || verified.current) return;
    verified.current = true;
    setNotice({ kind: 'info', text: c.verifying });
    verifyPayment(id, payment).then(async (r) => {
      // Growth OS (step 3): how the gateway visit ended. The paid fact itself is the server's.
      let at = null;
      try { at = Number(sessionStorage.getItem(`maabar.growth.pay.${id}`)) || null; sessionStorage.removeItem(`maabar.growth.pay.${id}`); } catch { /* timing only */ }
      track('payment_returned', { order_id: id, result: r.paid ? 'paid' : (r.ok === false ? 'error' : 'not_paid'), seconds_at_gateway: at ? Math.round((Date.now() - at) / 1000) : undefined });
      setNotice(r.paid ? { kind: 'ok', text: c.verifiedOk } : { kind: 'warn', text: c.verifiedNo });
      setParams({}, { replace: true });
      await load();
    });
  }, [params, id, c, load, setParams]);

  const act = async (fn) => {
    setBusy('act');
    const r = await fn();
    setBusy(''); setMode(''); setText('');
    if (!r.ok) setNotice({ kind: 'warn', text: c.error });
    await load();
  };
  const pay = async () => {
    setBusy('pay');
    const r = await startPayment(id, `${window.location.origin}/market/orders/${id}`, lang);
    if (r.ok && r.url) {
      track('payment_started', { order_id: id, amount_sar: data?.order?.total_sar, gateway: 'telr' });
      try { sessionStorage.setItem(`maabar.growth.pay.${id}`, String(Date.now())); } catch { /* timing only */ }
      window.location.assign(r.url);
      return;
    }
    setBusy(''); setNotice({ kind: 'warn', text: c.error });
  };

  if (!data) return <MarketShell lang={lang} user={user}><div className="fx-wrap"><p style={{ color: 'var(--text-secondary)' }}>…</p></div></MarketShell>;
  const o = data.order;
  const when = (iso) => new Date(iso).toLocaleString(isAr ? 'ar-SA' : lang === 'zh' ? 'zh-CN' : 'en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const section = { border: '1px solid var(--border)', borderRadius: 16, padding: 16, background: 'var(--surface-raised)', marginBottom: 14 };
  const h2 = { fontSize: 15.5, margin: '0 0 12px', fontFamily: font };
  const link = { background: 'none', border: 'none', padding: 0, color: '#9A7636', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, fontFamily: font };
  const primary = (on = true) => ({ padding: '10px 20px', borderRadius: 12, border: 'none', fontSize: 14, fontWeight: 600, fontFamily: font, background: on ? '#1A1814' : 'var(--border)', color: on ? '#fff' : 'var(--text-muted)', cursor: on ? 'pointer' : 'not-allowed' });

  return (
    <MarketShell lang={lang} user={user}>
      <div className="fx-wrap">
        {MARKET_API_IS_LOCAL && <PreviewBanner text={c.preview} isAr={isAr} />}
        <button className={`fx-back${isAr ? ' ar' : ''}`} onClick={() => nav('/market/orders')}>{isAr ? '→ ' : '← '}{c.back}</button>
        {!o ? <p style={{ color: 'var(--text-secondary)', fontFamily: font }}>{c.notFound}</p> : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '6px 0 14px' }}>
              <h1 className={`fx-h1${isAr ? ' ar' : ''}`} style={{ fontSize: isAr ? 24 : 27, margin: 0 }} dir="ltr">{o.order_no}</h1>
              <StatusBadge status={customerStep(o.status)} label={stepLabel(customerStep(o.status), lang)} />
            </div>
            {notice && <div data-notice={notice.kind} style={{ padding: '10px 14px', borderRadius: 12, marginBottom: 12, fontSize: 13.5, fontFamily: font,
              background: notice.kind === 'ok' ? '#EAF5EC' : notice.kind === 'warn' ? '#FFF4F2' : '#EEF2F8', color: notice.kind === 'ok' ? '#2E7D32' : notice.kind === 'warn' ? '#8A2A1C' : '#2B4A7A' }}>{notice.text}</div>}

            {/* The seven customer steps; side states (awaiting price, refund, …) show as the badge above. */}
            {CUSTOMER_STEPS.includes(customerStep(o.status)) && (
              <ol data-progress={customerStep(o.status)} style={{ display: 'flex', gap: 4, listStyle: 'none', padding: 0, margin: '0 0 16px', flexWrap: 'wrap', fontFamily: font }}>
                {CUSTOMER_STEPS.map((s, i) => {
                  const done = CUSTOMER_STEPS.indexOf(customerStep(o.status)) >= i;
                  return <li key={s} data-step={s} data-done={done ? 1 : 0} style={{ flex: '1 1 80px', fontSize: 11.5, color: done ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    <div style={{ height: 4, borderRadius: 2, background: done ? '#9A7636' : 'var(--border)', marginBottom: 5 }} />{stepLabel(s, lang)}</li>;
                })}
              </ol>
            )}
            {o.status === 'awaiting_shipping_quote' && <div data-awaiting-shipping="1" style={{ padding: '10px 14px', borderRadius: 12, marginBottom: 12, fontSize: 13.5, fontFamily: font, background: '#EEF2F8', color: '#2B4A7A' }}>{c.awaitingShip}</div>}
            {o.status === 'refund_pending' && <div data-refund-pending="1" style={{ padding: '10px 14px', borderRadius: 12, marginBottom: 12, fontSize: 13.5, fontFamily: font, background: '#FFF4F2', color: '#8A2A1C' }}>{c.refundPending}</div>}

            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start', fontFamily: font }}>
              <div style={{ flex: '1 1 560px', minWidth: 0 }}>
                {['pending_payment', 'awaiting_shipping_quote'].includes(o.status) && (
                  <section style={section} data-pay-box="1">
                    <h2 style={h2}>{c.payment}</h2>
                    {o.pay_by && <p style={{ fontSize: 13, margin: '0 0 10px', color: 'var(--text-secondary)' }}>{c.payBy(when(o.pay_by))}</p>}
                    {o.status !== 'pending_payment' ? null : config?.payments_enabled
                      ? <button type="button" data-pay-now="1" disabled={busy === 'pay'} onClick={pay} style={primary(busy !== 'pay')}>{c.payNow} · {formatSar(Number(o.total_sar))}</button>
                      : <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{c.paymentsOff}</p>}
                    <div style={{ marginTop: 10 }}>
                      {mode === 'cancel' ? (
                        <div>
                          <input data-cancel-reason="1" value={text} onChange={(e) => setText(e.target.value)} placeholder={c.cancelReason}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border-strong)', fontFamily: font, marginBottom: 8 }} />
                          <button type="button" data-confirm-cancel="1" disabled={busy === 'act'} onClick={() => act(() => cancelOrder(o.id, text))} style={{ ...primary(), background: '#8A2A1C' }}>{c.confirmCancel}</button>
                        </div>
                      ) : <button type="button" data-cancel="1" onClick={() => setMode('cancel')} style={link}>{c.cancel}</button>}
                    </div>
                  </section>
                )}

                {o.status === 'delivered' && (
                  <section style={section}>
                    <button type="button" data-confirm-received="1" disabled={busy === 'act'} onClick={() => act(() => confirmDelivery(o.id))} style={primary()}>{c.received}</button>
                  </section>
                )}
                {['shipped', 'delivered'].includes(o.status) && (
                  <section style={section}>
                    {mode === 'dispute' ? (
                      <div>
                        <textarea data-dispute-reason="1" value={text} onChange={(e) => setText(e.target.value)} placeholder={c.problemReason} rows={3}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border-strong)', fontFamily: font, marginBottom: 8 }} />
                        <button type="button" data-send-dispute="1" disabled={busy === 'act' || text.trim().length < 10} onClick={() => act(() => openDispute(o.id, text))} style={primary(text.trim().length >= 10)}>{c.send}</button>
                      </div>
                    ) : <button type="button" data-open-dispute="1" onClick={() => setMode('dispute')} style={link}>{c.problem}</button>}
                  </section>
                )}

                <section style={section} data-order-items="1">
                  <h2 style={h2}>{c.items}</h2>
                  {data.lines.map((l) => (
                    <div key={l.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
                      {l.image_ref && <img src={marketImageUrl(l.image_ref, true)} alt="" style={{ width: 46, height: 46, borderRadius: 8, objectFit: 'cover' }} />}
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', color: 'var(--text-primary)' }}>{(isAr ? l.title_ar : lang === 'zh' ? l.title_zh : l.title_en) || l.title_en || l.title_ar}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{(isAr ? l.variant_ar : lang === 'zh' ? l.variant_zh : l.variant_en) || ''} <span dir="ltr">{l.qty.toLocaleString('en-US')} × {formatSar(Math.round(Number(l.unit_sar) * feeUp(o) * 100) / 100)}</span></span>
                      </span>
                      <strong>{formatSar(Math.round(Number(l.line_sar) * feeUp(o) * 100) / 100)}</strong>
                    </div>
                  ))}
                </section>

                <section style={section} data-tracking="1">
                  <h2 style={h2}>{c.tracking}</h2>
                  {!data.shipments.length ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{c.noTracking}</p> : data.shipments.map((s) => (
                    <div key={s.id} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{s.leg === 'domestic' ? c.legDomestic : c.legIntl}</div>
                      {(s.carrier || s.tracking_number) && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {s.carrier && <span>{c.carrier}: {s.carrier} · </span>}{s.tracking_number && <span>{c.trackingNo}: <span dir="ltr">{s.tracking_number}</span></span>}</div>}
                      <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0' }}>
                        {[...(s.market_tracking_events || [])].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)).map((e) => (
                          <li key={e.id} data-track-source={e.source} style={{ fontSize: 12.5, padding: '4px 0', borderTop: '1px dashed var(--border)' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{when(e.occurred_at)}</span> · {e.title}
                            <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{e.source === 'admin' ? c.srcAdmin : c.srcProvider}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </section>

                <section style={section} data-timeline="1">
                  <h2 style={h2}>{c.timeline}</h2>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {data.events.filter((e) => e.kind === 'status' && customerStep(e.to_status) !== customerStep(e.from_status)).map((e) => (
                      <li key={e.id} style={{ fontSize: 12.5, padding: '5px 0', borderTop: '1px solid var(--border)' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{when(e.created_at)}</span> · {stepLabel(customerStep(e.to_status), lang)}
                        {e.note && e.visible_to_buyer && <span style={{ display: 'block', color: 'var(--text-secondary)' }}>{e.note}</span>}
                      </li>
                    ))}
                  </ul>
                </section>
              </div>

              <aside style={{ flex: '0 1 340px', minWidth: 260 }}>
                <section style={section} data-charges="1">
                  <h2 style={h2}>{c.charges}</h2>
                  {/* never a fee line (owner, 2026-11-02): the products carry it, «الشحن والجمارك» carries import charges */}
                  <Row k={c.products} v={<span data-order-products={orderProducts(o)}>{formatSar(orderProducts(o))}</span>} />
                  <Row k={c.shipCustoms} v={o.shipping_sar == null ? <span style={{ color: 'var(--text-muted)' }}>{c.shippingLater}</span> : formatSar(Math.round((Number(o.shipping_sar) + Number(o.import_sar || 0)) * 100) / 100)} />
                  {Number(o.services_sar) > 0 && <Row k={c.servicesLine} v={formatSar(Number(o.services_sar))} />}
                  {Number(o.vat_sar) > 0 && <Row k={c.vat} v={formatSar(Number(o.vat_sar))} />}
                  <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6 }}>
                    <Row k={<strong>{c.total}</strong>} v={<strong data-order-total={o.total_sar ?? ''}>{o.total_sar == null ? '—' : formatSar(Number(o.total_sar))}</strong>} />
                  </div>
                  {o.status === 'refunded' && <p data-refunded="1" style={{ fontSize: 12.5, color: '#2E7D32' }}>{c.refundDone}</p>}
                  {data.payments.length > 0 && (
                    <div style={{ marginTop: 10, fontSize: 12 }}>
                      {data.payments.map((p) => (
                        <div key={p.id} data-payment={p.status} style={{ color: 'var(--text-secondary)' }}>
                          {when(p.created_at)} · {formatSar(Number(p.amount_sar))} · {c[p.status] || p.status}{p.mode !== 'live' ? ` · ${c.payTest}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
                <section style={section}>
                  <h2 style={h2}>{c.delivery}</h2>
                  <div data-clarity-mask="True" style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)' }}>
                    <div>{o.delivery?.name}</div><div dir="ltr" style={{ textAlign: isAr ? 'right' : 'left' }}>{o.delivery?.phone}</div>
                    <div>{o.delivery?.district} · {o.delivery?.city}</div><div>{o.delivery?.address}</div>
                    {o.delivery?.shortAddress && <div dir="ltr" style={{ textAlign: isAr ? 'right' : 'left' }}>{o.delivery.shortAddress}</div>}
                  </div>
                </section>
              </aside>
            </div>
          </>
        )}
      </div>
    </MarketShell>
  );
}
